/**
 *
 * Supersample Anti-Aliasing / Temporal Anti-Aliasing Pass
 *
 * This pass uses a child EffectComposer. It will accumulate several samples of the preceding render passes from the
 * child EffectComposer and combine them. Camera position will get jittered for each sample so that the accumulated
 * result is antialiased. This also includes temporal antialiasing - if accumulateOverFrames is turned on, then
 * accumulated frames from previously rendered frames are also kept.
 *
 * References: https://en.wikipedia.org/wiki/Supersampling
 *
 * Based on Three.js SSAARenderPass and TAAPass. Modifications copyright Higharc Inc. 2019
 *
 */
import { assertIsNotNull, assertIsNotUndefined, assertIsOneOf } from "common/utils/assertOfType";
import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import { safeShaderReplace } from "../../materials/shaderUtils";
import { TONE_MAPPING_ALGORITHM } from "../../sceneLights";
import { effectComposerRender, type EffectComposer } from "../examples/jsm/postprocessing/EffectComposer";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import type { DepthNormalPass } from "./DepthNormalPass";
import { SSAAAccumulateShader } from "./SSAAAccumulateShader";
import { SSAAOutputShader } from "./SSAAOutputShader";
import { setToneMappingDefine } from "./ToneMappingDefines";

export interface DOFParams {
  applyDepthOfField: boolean;
  apertureRadius: number;
  cameraFocusDistance: number;
}

export const defaultDOFParams: DOFParams = {
  applyDepthOfField: false,
  cameraFocusDistance: 240.0,
  apertureRadius: 2.0,
};

// onBeforeRender is called each time before childEffectComposer renders. It takes the sample index as a parameter.
// onBeforeOutput is called each time before the final output is rendered. It takes the output shader uniforms and the material as a parameter.
// outputShaderCustomization is an object containing the following keys to customize the output shader:
//   "uniforms": list of custom uniforms to be added to the shader uniforms.
//   "adjustColorParams": added to global scope of the fragment shader if set.
//   "adjustColor": added to the end of the fragment shader main function if set.
//
export class SSAAPass extends EffectComposerPass {
  declare childEffects: EffectComposer;

  private renderer: THREE.WebGLRenderer;
  camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;

  private applyDepthOfField: boolean = defaultDOFParams.applyDepthOfField;
  private cameraFocusDistance: number = defaultDOFParams.cameraFocusDistance;
  private apertureRadius: number = defaultDOFParams.apertureRadius;
  private cameraBasePosition: THREE.Vector3 = new THREE.Vector3();
  private cameraBaseQuaternion: THREE.Quaternion = new THREE.Quaternion();
  private apertureOffset: THREE.Vector3 = new THREE.Vector3();
  private rotatedApertureOffset: THREE.Vector3 = new THREE.Vector3();

  useHalfFloatLinearRenderBuffer: boolean;

  // Specified as n, where the number of samples is 2^n, so sampleLevel = 4, is 2^4 samples, 16.
  // This can be clamped to a lower value if using a low bit depth render target so we avoid quantization artifacts.
  sampleLevel: number = 5;
  samplesPerFrame: number = 4;
  accumulateOverFrames: boolean = true; // Controls accumulation over multiple frames when camera/scene is still.
  accumulationRenderTargetIsClear: boolean = false;

  toneMappingEnabled: boolean = true;
  toneMappingExposure: number = 1.0;
  reprojectionTAA: boolean = true;
  private reprojectionTAAApplied: boolean = true;

  currentSampleMinWeight: number = 1 / 6; // Should be between 0 and 1.

  private accumulationRenderTargetSampleLevel = 0;
  private accumulateIndex: number = 0;
  private jitterIndex: number = 0;

  private onBeforeRender: ((accumulateIndex: number) => void) | undefined;
  private onBeforeOutput: ((outputUniforms: object, outputMaterial: THREE.Material) => void) | undefined;

  private addSampleUniforms;
  private addSamplePerspectiveMaterial: THREE.ShaderMaterial;
  private addSampleOrthographicMaterial: THREE.ShaderMaterial;
  private reprojectSampleUniforms;
  private reprojectSamplePerspectiveMaterial: THREE.ShaderMaterial;
  private reprojectSampleOrthographicMaterial: THREE.ShaderMaterial;
  private outputUniforms;
  private outputMaterial: THREE.ShaderMaterial;
  private outputQuad: FullScreenQuad;

  private addSampleQuad: FullScreenQuad;

  private resolution: THREE.Vector2;

  private sampleRenderTargetType: THREE.TextureDataType = THREE.UnsignedByteType;
  private accumulationRenderTargetType: THREE.TextureDataType;

  private sampleRenderTarget: THREE.WebGLRenderTarget | null = null;
  private accumulationRenderTarget: THREE.WebGLRenderTarget | null = null;
  private previousAccumulationRenderTarget: THREE.WebGLRenderTarget | null = null;
  private singleFrameAccumulationRenderTarget: THREE.WebGLRenderTarget | null = null;

  private depthNormalPass: DepthNormalPass;
  private prevViewMatrix: THREE.Matrix4 = new THREE.Matrix4();
  private prevViewProjectionMatrix: THREE.Matrix4 = new THREE.Matrix4();
  private prevViewMatrixInitialized: boolean = false;
  private cameraUnjitteredProjectionMatrix: THREE.Matrix4 = new THREE.Matrix4();

  toneMapping: THREE.ToneMapping = TONE_MAPPING_ALGORITHM;

  constructor(
    renderer: THREE.WebGLRenderer,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    useHalfFloatLinearRenderBuffer: boolean,
    childEffectComposer: EffectComposer,
    resolution: THREE.Vector2 | undefined,
    onBeforeRender: ((accumulateIndex: number) => void) | undefined,
    onBeforeOutput: ((outputUniforms: object, outputMaterial: THREE.Material) => void) | undefined,
    outputShaderCustomization: any,
    depthNormalPass: DepthNormalPass,
  ) {
    super();

    this.renderer = renderer;
    this.camera = camera;
    this.useHalfFloatLinearRenderBuffer = useHalfFloatLinearRenderBuffer;
    this.childEffects = childEffectComposer;
    this.depthNormalPass = depthNormalPass;

    this.onBeforeRender = onBeforeRender;
    this.onBeforeOutput = onBeforeOutput;

    this.addSampleUniforms = THREE.UniformsUtils.clone(SSAAAccumulateShader.uniforms);

    this.addSamplePerspectiveMaterial = new THREE.ShaderMaterial({
      uniforms: this.addSampleUniforms,
      vertexShader: SSAAAccumulateShader.vertexShader,
      fragmentShader: SSAAAccumulateShader.fragmentShader,
      defines: { ...SSAAAccumulateShader.defines },
      premultipliedAlpha: true,
      transparent: false,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "SSAAPass addSamplePerspectiveMaterial",
    });
    // This controls whether tonemapping will be performed on the incoming samples, not the shader output.
    const toneMapping: THREE.ToneMapping = this.useHalfFloatLinearRenderBuffer
      ? TONE_MAPPING_ALGORITHM
      : THREE.NoToneMapping;
    setToneMappingDefine(this.addSamplePerspectiveMaterial.defines as any, toneMapping);
    this.addSampleOrthographicMaterial = new THREE.ShaderMaterial({
      uniforms: this.addSampleUniforms,
      vertexShader: SSAAAccumulateShader.vertexShader,
      fragmentShader: SSAAAccumulateShader.fragmentShader,
      defines: { ...SSAAAccumulateShader.defines, PERSPECTIVE_CAMERA: 0 },
      premultipliedAlpha: true,
      transparent: false,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "SSAAPass addSampleOrthographicMaterial",
    });
    setToneMappingDefine(this.addSampleOrthographicMaterial.defines as any, toneMapping);
    this.reprojectSamplePerspectiveMaterial = this.addSamplePerspectiveMaterial.clone();
    this.reprojectSampleOrthographicMaterial = this.addSampleOrthographicMaterial.clone();
    this.reprojectSampleUniforms = THREE.UniformsUtils.clone(SSAAAccumulateShader.uniforms);
    this.reprojectSamplePerspectiveMaterial.uniforms = this.reprojectSampleUniforms;
    this.reprojectSampleOrthographicMaterial.uniforms = this.reprojectSampleUniforms;

    this.addSampleQuad = new FullScreenQuad(this.addSamplePerspectiveMaterial);

    const outputShader = SSAAOutputShader;
    this.outputUniforms = THREE.UniformsUtils.clone(outputShader.uniforms);

    let outputFragmentShader = outputShader.fragmentShader;
    if (outputShaderCustomization !== undefined) {
      if (outputShaderCustomization.uniforms !== undefined) {
        for (const key in outputShaderCustomization.uniforms) {
          if (outputShaderCustomization.uniforms.hasOwnProperty(key)) {
            this.outputUniforms[key] = outputShaderCustomization.uniforms[key];
          }
        }
      }
      if (outputShaderCustomization.adjustColorParams !== undefined) {
        outputFragmentShader = safeShaderReplace(
          outputFragmentShader,
          "// <adjust color params>",
          outputShaderCustomization.adjustColorParams,
          "SSAAPass outputShader (adjust color params)",
        );
      }
      if (outputShaderCustomization.adjustColor !== undefined) {
        outputFragmentShader = safeShaderReplace(
          outputFragmentShader,
          "// <adjust color>",
          outputShaderCustomization.adjustColor,
          "SSAAPass outputShader (adjust color)",
        );
      }
    }
    this.outputMaterial = new THREE.ShaderMaterial({
      uniforms: this.outputUniforms,
      vertexShader: outputShader.vertexShader,
      fragmentShader: outputFragmentShader,
      premultipliedAlpha: true,
      transparent: false,
      blending: THREE.NoBlending,
      depthTest: false,
      depthWrite: false,
      name: "SSAAPass outputMaterial",
    });
    this.outputQuad = new FullScreenQuad(this.outputMaterial);

    this.resolution =
      resolution !== undefined ? new THREE.Vector2(resolution.x, resolution.y) : new THREE.Vector2(256, 256);

    this.accumulationRenderTargetType = THREE.UnsignedByteType;
    this.allocateRenderTargets();
  }

  private allocateSampleRenderTarget() {
    if (this.sampleRenderTarget) {
      this.sampleRenderTarget.dispose();
    }
    if (this.useHalfFloatLinearRenderBuffer) {
      this.sampleRenderTarget = new THREE.WebGLRenderTarget(this.resolution.x, this.resolution.y, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
        colorSpace: THREE.NoColorSpace,
        stencilBuffer: true,
      });
      this.sampleRenderTarget.texture.name = "SSAAPass.sample";
    } else {
      // Matching the renderer's output colorspace here means less quantization artifacts,
      // and prevents performance issues from switching materials in case some frames are rendered bypassing SSAA.
      const colorSpace = this.renderer.outputColorSpace;
      assertIsOneOf(colorSpace, [THREE.NoColorSpace, THREE.SRGBColorSpace, THREE.LinearSRGBColorSpace]);

      this.sampleRenderTarget = new THREE.WebGLRenderTarget(this.resolution.x, this.resolution.y, {
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        colorSpace,
        stencilBuffer: true,
      });
      this.sampleRenderTarget.texture.name = "SSAAPass.sample";
    }
  }

  private allocateRenderTargets() {
    this.allocateSampleRenderTarget();

    // The RGB channels of the render target store the accumulated value, and the alpha channel stores the accumulated sample weight.
    // Normally we're fairly resistant to quantization error, since we don't multiply the samples with small values as much, so we can use UnsignedByteType.
    // Filtering is set to linear since we support rendering at a different resolution from the final framebuffer.

    if (this.accumulationRenderTarget) {
      this.accumulationRenderTarget.dispose();
    }
    if (this.previousAccumulationRenderTarget) {
      this.previousAccumulationRenderTarget.dispose();
    }
    if (this.singleFrameAccumulationRenderTarget) {
      this.singleFrameAccumulationRenderTarget.dispose();
    }

    assertIsNotNull(this.sampleRenderTarget);
    const colorSpace = this.sampleRenderTarget.texture.colorSpace;
    assertIsOneOf(colorSpace, [THREE.NoColorSpace, THREE.SRGBColorSpace, THREE.LinearSRGBColorSpace]);

    this.accumulationRenderTarget = new THREE.WebGLRenderTarget(this.resolution.x, this.resolution.y, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: this.accumulationRenderTargetType,
      colorSpace,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.accumulationRenderTarget.texture.name = "SSAAPass.accumulationA";

    this.previousAccumulationRenderTarget = new THREE.WebGLRenderTarget(this.resolution.x, this.resolution.y, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: this.accumulationRenderTargetType,
      colorSpace,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.previousAccumulationRenderTarget.texture.name = "SSAAPass.accumulationB";

    this.singleFrameAccumulationRenderTarget = new THREE.WebGLRenderTarget(this.resolution.x, this.resolution.y, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: this.accumulationRenderTargetType,
      colorSpace,
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.singleFrameAccumulationRenderTarget.texture.name = "SSAAPass.accumulationC";

    this.accumulationRenderTargetSampleLevel = this.getUsedSampleLevel();
  }

  dispose() {
    this.sampleRenderTarget?.dispose();
    this.accumulationRenderTarget?.dispose();
    this.previousAccumulationRenderTarget?.dispose();
    this.singleFrameAccumulationRenderTarget?.dispose();

    this.accumulateIndex = 0;
    this.accumulationRenderTargetIsClear = false;
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);
    this.resolution = new THREE.Vector2(width, height);
    this.sampleRenderTarget?.setSize(width, height);
    this.accumulationRenderTarget?.setSize(width, height);
    this.previousAccumulationRenderTarget?.setSize(width, height);
    this.singleFrameAccumulationRenderTarget?.setSize(width, height);

    this.resetAccumulation();
  }

  isDoneAccumulatingSamples() {
    if (!this.accumulateOverFrames) {
      return false;
    }
    const totalSamples = Math.pow(2, this.getUsedSampleLevel());
    return this.accumulateIndex >= totalSamples;
  }

  resetAccumulation() {
    this.accumulateIndex = 0;
    this.jitterIndex = 0;
    this.accumulationRenderTargetSampleLevel = this.getUsedSampleLevel();
    this.prevViewMatrixInitialized = false;

    if (!this.accumulationRenderTargetIsClear) {
      assertIsNotUndefined(this.accumulationRenderTarget);
      this.renderer.setRenderTarget(this.accumulationRenderTarget);
      this.renderer.setClearColor(0x000000, 0.0);
      this.renderer.clear();
      this.accumulationRenderTargetIsClear = true;
    }
    this.clearSingleFrameAccumulationTarget();
  }

  clearSingleFrameAccumulationTarget() {
    assertIsNotUndefined(this.singleFrameAccumulationRenderTarget);
    this.renderer.setRenderTarget(this.singleFrameAccumulationRenderTarget);
    this.renderer.setClearColor(0x000000, 0.0);
    this.renderer.clear();
  }

  beginFrame() {
    if (this.reprojectionTAAApplied) {
      this.clearSingleFrameAccumulationTarget();
      // First sample of the frame is not added to the single frame accumulation buffer to maintain maximum performance while interacting with the canvas.
      this.accumulateIndex = -1;
    } else {
      this.resetAccumulation();
    }
  }

  _isApplyingDepthOfField() {
    return this.applyDepthOfField && this.getUsedSampleLevel() > 3;
  }

  getUsedSampleLevel() {
    const sampleLevel = Math.max(this.sampleLevel, 0);
    return sampleLevel;
  }

  accumulateSampleIfNeeded(renderer: THREE.WebGLRenderer) {
    const usedSampleLevel = this.getUsedSampleLevel();

    if (this.accumulationRenderTargetSampleLevel !== usedSampleLevel) {
      this.resetAccumulation();
    }

    if (this.reprojectionTAAApplied !== this.reprojectionTAA) {
      this.resetAccumulation();
      this.reprojectionTAAApplied = this.reprojectionTAA;
    }

    const sampleRenderTargetType = this.useHalfFloatLinearRenderBuffer ? THREE.HalfFloatType : THREE.UnsignedByteType;
    if (this.sampleRenderTargetType !== sampleRenderTargetType) {
      this.sampleRenderTargetType = sampleRenderTargetType;
      this.allocateSampleRenderTarget();
    }

    const totalSamples = Math.pow(2, usedSampleLevel);
    if (this.accumulateIndex >= totalSamples) {
      return;
    }

    this.cameraBasePosition.copy(this.camera.position);
    this.cameraBaseQuaternion.copy(this.camera.quaternion);

    const jitterOffset = getJitterOffset(usedSampleLevel, this.jitterIndex);

    const perspectiveCamera = this.camera as THREE.PerspectiveCamera;

    const cameraOriginalView =
      perspectiveCamera.view && perspectiveCamera.view.enabled ? { ...perspectiveCamera.view } : undefined;

    if (this.camera.setViewOffset) {
      const view = cameraOriginalView ?? {
        fullWidth: this.resolution.x,
        fullHeight: this.resolution.y,
        offsetX: 0,
        offsetY: 0,
        width: this.resolution.x,
        height: this.resolution.y,
      };
      this.camera.setViewOffset(view.fullWidth, view.fullHeight, view.offsetX, view.offsetY, view.width, view.height);
      this.cameraUnjitteredProjectionMatrix.copy(this.camera.projectionMatrix);
      if (this._isApplyingDepthOfField() && !cameraOriginalView) {
        if (this.accumulateIndex % 2 === 0) {
          let sampleAngle = Math.random() * 2 * Math.PI;
          let sampleRadius = Math.random();
          const samplesInSpiral = Math.min(64, totalSamples);
          if (this.accumulateIndex < samplesInSpiral) {
            const samplesPerRing = 5.0;
            sampleAngle = ((this.accumulateIndex / 2 + Math.random()) / samplesPerRing) * 2.0 * Math.PI;
            const randomRadiusMult = 1.0 + (Math.random() - 0.5) * 0.2;
            sampleRadius = (this.accumulateIndex / samplesInSpiral) * randomRadiusMult;
          }
          if (sampleRadius > 0.0) {
            // Sqrt is needed here so that we get an even distribution inside the circle of the aperture.
            sampleRadius = Math.sqrt(sampleRadius) * this.apertureRadius;
          }
          this.apertureOffset.set(Math.sin(sampleAngle) * sampleRadius, Math.cos(sampleAngle) * sampleRadius, 0.0);
        } else {
          this.apertureOffset.multiplyScalar(-1.0);
        }

        // TOOD: Handle rotation of the camera parent correctly.
        this.camera.position.copy(this.cameraBasePosition);
        this.rotatedApertureOffset.copy(this.apertureOffset);
        this.rotatedApertureOffset.applyQuaternion(this.cameraBaseQuaternion);
        this.camera.position.add(this.rotatedApertureOffset);

        let top =
          (perspectiveCamera.near * Math.tan(((0.5 * perspectiveCamera.fov) / 180) * Math.PI)) / perspectiveCamera.zoom;
        const height = 2 * top;
        const width = perspectiveCamera.aspect * height;
        let left = -0.5 * width;

        // Reference for depth of field offsets:
        // https://www.opengl.org/archives/resources/code/samples/advanced/advanced97/notes/node87.html
        left +=
          (-this.apertureOffset.x * perspectiveCamera.near) / this.cameraFocusDistance +
          (jitterOffset[0] * width) / this.resolution.x;
        top +=
          (-this.apertureOffset.y * perspectiveCamera.near) / this.cameraFocusDistance -
          (jitterOffset[1] * height) / this.resolution.y;
        this.camera.projectionMatrix.makePerspective(
          left,
          left + width,
          top,
          top - height,
          perspectiveCamera.near,
          perspectiveCamera.far,
        );
        this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();
      } else {
        this.camera.setViewOffset(
          view.fullWidth,
          view.fullHeight,
          view.offsetX + jitterOffset[0] * (view.width / this.resolution.x),
          view.offsetY + jitterOffset[1] * (view.height / this.resolution.y),
          view.width,
          view.height,
        );
      }
    }

    if (this.onBeforeRender !== undefined) {
      this.onBeforeRender(this.accumulateIndex);
    }

    effectComposerRender(this.childEffects, this.renderer, this.sampleRenderTarget);

    const autoClear = renderer.autoClear;

    const isPerspectiveCamera = (this.camera as THREE.PerspectiveCamera).isPerspectiveCamera;

    if (!this.reprojectionTAAApplied || this.accumulateIndex >= 0) {
      // Accumulate samples of the same frame into a buffer that doesn't include any reprojected TAA samples.
      // Once enough samples have been accumulated, we'll only show this buffer to ensure that there are no ghosting artifacts due to reprojected samples.

      // Swap render targets.
      const temp = this.singleFrameAccumulationRenderTarget;
      this.singleFrameAccumulationRenderTarget = this.previousAccumulationRenderTarget;
      this.previousAccumulationRenderTarget = temp;

      const addSampleMaterial = isPerspectiveCamera
        ? this.addSamplePerspectiveMaterial
        : this.addSampleOrthographicMaterial;

      addSampleMaterial.defines["REPROJECT"] = 0;
      if (
        setToneMappingDefine(
          addSampleMaterial.defines as any,
          this.useHalfFloatLinearRenderBuffer ? this.toneMapping : THREE.NoToneMapping,
        )
      ) {
        addSampleMaterial.needsUpdate = true;
      }
      this.addSampleUniforms.ssfxToneMappingEnabled.value = this.toneMappingEnabled; // Runtime switch for fast switching between linework and HDR modes for example
      this.addSampleUniforms.toneMappingExposure.value = this.toneMappingExposure;
      this.addSampleUniforms["tSample"].value = this.sampleRenderTarget?.texture;

      const sampleWeight =
        this.accumulateIndex === 0 ? 1.0 : this.jitterSampleWeight(1.0 / (this.accumulateIndex + 1.0), totalSamples);
      this.addSampleUniforms["weight"].value = sampleWeight;

      this.addSampleUniforms["tPrevious"].value = this.previousAccumulationRenderTarget?.texture;

      assertIsNotUndefined(this.singleFrameAccumulationRenderTarget);

      renderer.autoClear = false;
      renderer.setRenderTarget(this.singleFrameAccumulationRenderTarget);
      //console.log("Accumulating single frame from " + this.previousAccumulationRenderTarget.texture.name + " to " + this.singleFrameAccumulationRenderTarget.texture.name);
      this.addSampleQuad.material = addSampleMaterial;
      this.addSampleQuad.render(renderer);
    }

    if (this.reprojectionTAAApplied) {
      // Swap render targets.
      const tempB = this.accumulationRenderTarget;
      this.accumulationRenderTarget = this.previousAccumulationRenderTarget;
      this.previousAccumulationRenderTarget = tempB;

      const reprojectSampleMaterial = isPerspectiveCamera
        ? this.reprojectSamplePerspectiveMaterial
        : this.reprojectSampleOrthographicMaterial;

      const hasViewZ = this.depthNormalPass.params.enableViewZConversion;
      const hasPrevViewZ = hasViewZ && this.depthNormalPass.storePrevViewZRenderTarget;

      reprojectSampleMaterial.defines["REPROJECT"] = 1;
      reprojectSampleMaterial.defines["DEPTH_PACKING"] = this.depthNormalPass.isDepthRGBAPacked() ? 1 : 0;
      reprojectSampleMaterial.defines["DEPTH_TEX_CONTAINS_VIEWZ"] = hasViewZ ? 1 : 0;
      reprojectSampleMaterial.defines["USE_PREV_DEPTH_IN_REPROJECT"] = hasPrevViewZ ? 1 : 0;

      const sampleIsSingleSample = this.accumulateIndex === -1;
      const sampleRenderTarget = sampleIsSingleSample
        ? this.sampleRenderTarget
        : this.singleFrameAccumulationRenderTarget;
      this.reprojectSampleUniforms["tSample"].value = sampleRenderTarget?.texture;

      if (
        setToneMappingDefine(
          reprojectSampleMaterial.defines as any,
          this.useHalfFloatLinearRenderBuffer ? this.toneMapping : THREE.NoToneMapping,
        )
      ) {
        reprojectSampleMaterial.needsUpdate = true;
      }

      this.reprojectSampleUniforms.ssfxToneMappingEnabled.value = sampleIsSingleSample && this.toneMappingEnabled; // Runtime switch for fast switching
      this.reprojectSampleUniforms.toneMappingExposure.value = this.toneMappingExposure;

      this.reprojectSampleUniforms["weight"].value = this.jitterSampleWeight(
        this.currentSampleMinWeight +
          ((1 - this.currentSampleMinWeight) * Math.max(this.accumulateIndex, 0)) / totalSamples,
        totalSamples,
      );
      this.reprojectSampleUniforms["tPrevious"].value = this.previousAccumulationRenderTarget?.texture;

      const depthTexture = hasViewZ ? this.depthNormalPass.viewZTexture : this.depthNormalPass.depthTexture;
      this.reprojectSampleUniforms["tDepth"].value = depthTexture;
      if (hasPrevViewZ) {
        this.reprojectSampleUniforms["tPrevDepth"].value = this.depthNormalPass.prevViewZTexture;
      }
      this.reprojectSampleUniforms["cameraNear"].value = perspectiveCamera.near;
      this.reprojectSampleUniforms["cameraFar"].value = perspectiveCamera.far;
      this.reprojectSampleUniforms["cameraProjectionMatrix"].value.copy(this.cameraUnjitteredProjectionMatrix);
      this.reprojectSampleUniforms["cameraInverseProjectionMatrix"].value
        .copy(this.cameraUnjitteredProjectionMatrix)
        .invert();
      this.reprojectSampleUniforms["inverseViewMatrix"].value.copy(this.camera.matrixWorld);
      if (!this.prevViewMatrixInitialized) {
        this.prevViewMatrixInitialized = true;
        this.prevViewMatrix.copy(this.camera.matrixWorldInverse);
        this.prevViewProjectionMatrix.multiplyMatrices(
          this.cameraUnjitteredProjectionMatrix,
          this.camera.matrixWorldInverse,
        );
      }
      this.reprojectSampleUniforms["prevViewMatrix"].value.copy(this.prevViewMatrix);
      this.reprojectSampleUniforms["prevViewProjectionMatrix"].value.copy(this.prevViewProjectionMatrix);
      this.reprojectSampleUniforms["pixelSize"].value.set(1 / this.resolution.x, 1 / this.resolution.y);
      this.reprojectSampleUniforms["accumulateIndex"].value = this.accumulateIndex;

      this.prevViewMatrix.copy(this.camera.matrixWorldInverse);
      this.prevViewProjectionMatrix.multiplyMatrices(
        this.cameraUnjitteredProjectionMatrix,
        this.camera.matrixWorldInverse,
      );

      assertIsNotUndefined(this.accumulationRenderTarget);
      renderer.setRenderTarget(this.accumulationRenderTarget);
      //console.log("Accumulating TAA from " + this.previousAccumulationRenderTarget.texture.name + " to " + this.accumulationRenderTarget.texture.name);
      this.addSampleQuad.material = reprojectSampleMaterial;
      this.addSampleQuad.render(renderer);
    }

    renderer.autoClear = autoClear;

    this.accumulationRenderTargetIsClear = false;

    ++this.accumulateIndex;
    ++this.jitterIndex;

    if (cameraOriginalView !== undefined) {
      const newView = Object.assign({}, cameraOriginalView);
      this.camera.view = newView;
    } else {
      if (this.camera.clearViewOffset) {
        this.camera.clearViewOffset();
      }
    }

    this.camera.position.copy(this.cameraBasePosition);
  }

  jitterSampleWeight(baseSampleWeight: number, totalSamples: number) {
    if (baseSampleWeight === 1.0) {
      return 1.0;
    }
    // Equal weights for each sample lead to an accumulation of rounding errors.
    // The following equation varies the sampleWeight per sample so that it is uniformly distributed
    // across a range of values whose rounding errors cancel each other out.
    let uniformCenteredDistribution = ((this.jitterIndex % totalSamples) + 0.5) / totalSamples;
    if (this.jitterIndex % 2 === 0) {
      uniformCenteredDistribution = -uniformCenteredDistribution;
    }
    const roundingRange = 1 / 256;
    return baseSampleWeight + roundingRange * uniformCenteredDistribution;
  }

  renderOutput(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null, aabb?: AABB2) {
    if (this.accumulateIndex >= Math.pow(2, this.getUsedSampleLevel()) || !this.reprojectionTAAApplied) {
      this.outputUniforms["tDiffuse"].value = this.singleFrameAccumulationRenderTarget?.texture;
    } else {
      this.outputUniforms["tDiffuse"].value = this.accumulationRenderTarget?.texture;
    }
    renderer.setRenderTarget(writeBuffer);
    if (this.onBeforeOutput !== undefined) {
      this.onBeforeOutput(this.outputUniforms, this.outputMaterial);
    }
    if (aabb === undefined) {
      this.outputQuad.render(renderer);
      return;
    }
    this.outputQuad.renderPixelRectangle(renderer, aabb);
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    if (!this.accumulateOverFrames) {
      this.resetAccumulation();
    }

    // render the scene multiple times, each slightly jitter offset from the last and accumulate the results.
    for (let i = 0; i < this.samplesPerFrame; ++i) {
      this.accumulateSampleIfNeeded(renderer);
    }

    this.renderOutput(renderer, writeBuffer);
  }
}

// These jitter vectors are specified in integers because it is easier.
// The offsets are relative to a [-8,8] integer grid, but need to be mapped onto [-0.5,0.5].
//
// Sample patterns reference: https://msdn.microsoft.com/en-us/library/windows/desktop/ff476218%28v=vs.85%29.aspx?f=255&MSPPError=-2147217396
//
// Somewhat optimized so that picking a set of samples from beginning of each list still generates an OK result.
const fixedSamplePatterns = [
  [[0, 0]],
  [
    [4, 4],
    [-4, -4],
  ],
  [
    [-2, -6],
    [6, -2],
    [-6, 2],
    [2, 6],
  ],
];

function haltonSequence(base: number, count: number): number[] {
  const seq: number[] = [];
  let nom = 0,
    denom = 1;
  for (let i = 0; i < count; ++i) {
    const x = denom - nom;
    if (x === 1) {
      nom = 1;
      denom *= base;
    } else {
      let y = Math.floor(denom / base);
      while (x <= y) {
        y = Math.floor(y / base);
      }
      nom = (base + 1) * y - x;
    }
    seq.push(nom / denom);
  }
  return seq;
}

const cachedHalton2 = haltonSequence(2, 256);
const cachedHalton3 = haltonSequence(3, 256);

/**
 * Get jitter offset to use for taking an antialiasing sample.
 * @param {*} usedSampleLevel Total number of samples.
 * @param {*} sampleIndex Index of the sample.
 * @returns Jitter offset in the -0.5, 0.5 range.
 */
function getJitterOffset(usedSampleLevel: number, sampleIndex: number) {
  if (usedSampleLevel < fixedSamplePatterns.length) {
    const offsetsArray = fixedSamplePatterns[usedSampleLevel];
    const offset = [...offsetsArray[sampleIndex % offsetsArray.length]];
    offset[0] *= 0.0625; // 0.0625 = 1 / 16;
    offset[1] *= 0.0625; // 0.0625 = 1 / 16;
    return offset;
  }
  sampleIndex = sampleIndex % cachedHalton2.length;
  return [cachedHalton2[sampleIndex] - 0.5, cachedHalton3[sampleIndex] - 0.5];
}
